import { useNavigate } from '@tanstack/react-router';
import { GitBranchIcon, GitCompareIcon, XIcon } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { getTimeAgo } from '@/lib/time';
import { useZero } from '@/lib/zero';

export function PullSuggestionBanner({
  suggestion,
}: {
  suggestion: {
    id: string | null;
    owner: string;
    repo: string;
    branch: string;
    pusherLogin: string;
    pusherId: number;
    headSha: string;
    latestCommitMessage: string | null;
    latestCommitAuthor: string | null;
    pushedAt: number;
    dismissed: boolean | null;
    pullRequestCreated: boolean | null;
    pullRequestNumber: number | null;
    defaultBranch: string;
    createdAt: number | null;
    updatedAt: number | null;
  };
}) {
  const navigate = useNavigate();
  const z = useZero();

  const handleDismiss = async () => {
    // Update the dismissed flag in the database
    await z.mutate.BranchPushesTable.update({
      id: suggestion.id!,
      dismissed: true,
    });
  };

  const handleCreatePR = () => {
    // Generate title from commit message or branch name
    const title = suggestion.latestCommitMessage
      ? suggestion.latestCommitMessage.split('\n')[0] // First line of commit
      : `Update ${suggestion.branch}`;

    navigate({
      to: '/pulls/new',
      search: {
        owner: suggestion.owner,
        repo: suggestion.repo,
        base: suggestion.defaultBranch,
        head: suggestion.branch,
        title,
      },
    });
  };

  return (
    <div className="max-w-5xl mx-auto">
      <Alert className="mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 items-center">
        <GitBranchIcon className="h-4 w-4 text-blue-600 translate-y-0" />
        <AlertDescription className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Your recently pushed branch:</span>
            <span className="font-medium">{suggestion.branch}</span>
            <span className="text-sm text-muted-foreground">
              ({getTimeAgo(new Date(suggestion.pushedAt).toISOString())})
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={handleCreatePR}>
              <GitCompareIcon className="mr-1.5 h-3.5 w-3.5" />
              Compare & pull request
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 w-7 p-0"
              onClick={handleDismiss}
              aria-label="Dismiss suggestion"
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
}
